// Saves a PNG data URL to the user's device, or shares it, uniformly across
// platforms. iOS Safari does not support the `download` attribute on anchor
// tags — a plain `<a download>` click just opens/navigates instead of saving
// — so on iOS we go through the native Share Sheet (which can save to Photos)
// and fall back to opening the image in a new tab for a manual long-press
// save when the Share Sheet isn't available.
// Exported so callers can branch BEFORE they get here: anything that saves
// after an async gap (an AI render, a network fetch) can't rely on the Share
// Sheet, because iOS only allows it inside a live user gesture. Such callers
// need to park the result and let the user tap again.
export function isIOSDevice() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
         (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

export function saveOrShareImage(dataUrl, filename, shareTitle = 'Screenshot') {
  const isIOS = isIOSDevice();

  if (isIOS) {
    if (navigator.share && navigator.canShare) {
      try {
        const byteStr = atob(dataUrl.split(',')[1]);
        const ab = new ArrayBuffer(byteStr.length);
        const ia = new Uint8Array(ab);
        for (let i = 0; i < byteStr.length; i++) ia[i] = byteStr.charCodeAt(i);
        const file = new File([ab], filename, { type: 'image/png' });
        if (navigator.canShare({ files: [file] })) {
          navigator.share({ files: [file], title: shareTitle }).catch(() => {});
          return;
        }
      } catch { /* fall through to the new-tab fallback below */ }
    }
    const w = window.open(dataUrl, '_blank');
    if (!w) {
      const a = document.createElement('a');
      a.href = dataUrl; a.target = '_blank'; a.rel = 'noopener noreferrer';
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
    }
    return;
  }

  const a = document.createElement('a');
  a.href = dataUrl; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
}

// ─── LOGO WATERMARK ──────────────────────────────────────────────────────────
// The Tunify logo stamped into the bottom-right corner of every exported
// screenshot. Preloaded at module load so it's ready to draw synchronously at
// capture time — screenshots must stay within the user gesture (iOS Share
// Sheet) and can't await an image load mid-flight.
const LOGO_SRC = '/images/Tunify Original logo png.png';
const WATERMARK_OPACITY = 0.6;

const _logoImg = new Image();
let _logoReady = false;
_logoImg.onload = () => { _logoReady = true; };
_logoImg.src = LOGO_SRC;

// Draw the logo bottom-right on a 2D context whose drawing area is w×h (in the
// context's current units — pass the pre-scaled/CSS size, not device pixels).
// No-op until the logo has loaded, so a screenshot taken in the first instant
// after load simply ships without it rather than blocking.
export function drawLogoWatermark(ctx, w, h) {
  if (!_logoReady || !_logoImg.naturalWidth) return;
  const targetW = Math.max(56, Math.min(150, w * 0.15));
  const targetH = targetW * (_logoImg.naturalHeight / _logoImg.naturalWidth);
  // Small margin so the logo hugs the bottom-right corner.
  const margin = Math.max(2, w * 0.004);
  ctx.save();
  ctx.globalAlpha = WATERMARK_OPACITY;
  ctx.drawImage(_logoImg, w - targetW - margin, h - targetH - margin, targetW, targetH);
  ctx.restore();
}

// Composite a source canvas (e.g. a WebGL render whose context can't be drawn
// on directly) onto a fresh 2D canvas, stamp the logo, and return a PNG URL.
export function watermarkCanvasToDataUrl(srcCanvas) {
  const out = document.createElement('canvas');
  out.width = srcCanvas.width;
  out.height = srcCanvas.height;
  const ctx = out.getContext('2d');
  ctx.drawImage(srcCanvas, 0, 0);
  drawLogoWatermark(ctx, out.width, out.height);
  return out.toDataURL('image/png');
}
